import React, { useState, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";
import basketballIcon from "../assets/images/sk-logo.png";
import {
  Container, Box, Typography, TextField, Button, Paper, Grid, Alert,
  Avatar, Select, FormControl, InputLabel, MenuItem, FormHelperText,
  FormControlLabel, Radio, RadioGroup, Stepper, Step, StepLabel,
  Fade, InputAdornment, CircularProgress, useTheme, useMediaQuery,
  MobileStepper,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  List, ListItem, ListItemText, Divider, Chip
} from "@mui/material";
import {
  Person, Phone, Email, LocationOn, School,
  HowToVote, ArrowForward, ArrowBack, CheckCircle, KeyboardArrowRight, KeyboardArrowLeft,
  Warning, Check, Close
} from "@mui/icons-material";
import '../assets/styles/Main.scss';

const supabaseUrl = process.env.REACT_APP_SUPABASE_PROJECT_ID;
const supabaseKey = process.env.REACT_APP_SUPABASE_PUBLIC_ANON_KEY;
if (!supabaseKey) {
  console.error("Missing Supabase key. Make sure .env is configured correctly.");
}
const supabase = createClient(supabaseUrl || '', supabaseKey || '');

const SUBMISSION_TIMEOUT = 10000;
const SUBMISSION_STORAGE_KEY = 'youth_profile_last_submission_time';

const FORM_STEPS = [
  { label: 'Personal', color: '#1B75BC', icon: Person },
  { label: 'Contact', color: '#FDB813', icon: Phone },
  { label: 'Status', color: '#ED1C24', icon: School },
  { label: 'SK', color: '#4A5568', icon: HowToVote },
];

const captureDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  let browser = 'Unknown';
  let deviceType = 'Desktop';
  if (userAgent.indexOf('Chrome') > -1) browser = 'Chrome';
  else if (userAgent.indexOf('Safari') > -1) browser = 'Safari';
  else if (userAgent.indexOf('Firefox') > -1) browser = 'Firefox';
  else if (userAgent.indexOf('Edge') > -1) browser = 'Edge';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    deviceType = /iPad/i.test(userAgent) ? 'Tablet' : 'Mobile';
  }
  return {
    userAgent, platform: navigator.platform, language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    browser, deviceType, viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
};

const SUFFIX_OPTIONS = ['None', 'Jr.', 'Sr.', 'II', 'III', 'IV'];
const SEX_OPTIONS = ['Male', 'Female', 'Other'];
const CIVIL_STATUS_OPTIONS = ['Single', 'Married', 'Divorced', 'Separated', 'Widowed'];
const YOUTH_CLASSIFICATION_OPTIONS = ['NEET', 'WY', 'WYSN', 'PWD', 'IP'];
const EDUCATION_OPTIONS = ['Elementary', 'High School', 'Senior High', 'College', 'Vocational', 'Masteral', 'Doctorate', 'N/A'];
const WORK_STATUS_OPTIONS = ['Employed', 'Unemployed', 'Student', 'Self-employed', 'N/A'];
const ZONE_OPTIONS = ['Zone 1', 'Zone 2', 'Zone 3A', 'Zone 3B', 'Zone 4', 'Zone 5', 'Zone 6', 'Zone 7', 'Zone 8A', 'Zone 8B'];

function YouthProfilingForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';

  const [formData, setFormData] = useState({
    first_name: "", middle_name: "", last_name: "", suffix_name: "None", sex: "",
    birth_date: "", civil_status: "", email: "", contact_number: "", address: "",
    zone: "", youth_classification: "", highest_education: "", work_status: "",
    registered_voter: null as boolean | null, voted_last_election: null as boolean | null,
    attended_sk_assembly: null as boolean | null, assembly_attendance_count: "",
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [status, setStatus] = useState<{ success: boolean | null; message: string }>({ success: null, message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [duplicateCheck, setDuplicateCheck] = useState<{ loading: boolean; exists: boolean | null }>({ loading: false, exists: null });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  useEffect(() => {
    const lastSubmissionTime = localStorage.getItem(SUBMISSION_STORAGE_KEY);
    if (lastSubmissionTime) {
      const elapsedTime = Date.now() - parseInt(lastSubmissionTime, 10);
      if (elapsedTime < SUBMISSION_TIMEOUT) {
        setIsSubmitting(true);
        const remainingTime = Math.ceil((SUBMISSION_TIMEOUT - elapsedTime) / 1000);
        setCountdown(remainingTime);
        const countdownInterval = setInterval(() => {
          setCountdown(prevCount => {
            if (prevCount <= 1) {
              clearInterval(countdownInterval);
              setIsSubmitting(false);
              localStorage.removeItem(SUBMISSION_STORAGE_KEY);
              return 0;
            }
            return prevCount - 1;
          });
        }, 1000);
        return () => clearInterval(countdownInterval);
      } else {
        localStorage.removeItem(SUBMISSION_STORAGE_KEY);
      }
    }
  }, []);

  const checkDuplicateContact = async (contactNumber: string) => {
    if (!contactNumber || contactNumber.length < 10) return;
    setDuplicateCheck({ loading: true, exists: null });
    try {
      const { data, error } = await supabase.from('youth_profiles').select('id').eq('contact_number', contactNumber).limit(1);
      if (error) throw error;
      setDuplicateCheck({ loading: false, exists: data && data.length > 0 });
    } catch (error) {
      console.error('Error checking duplicate:', error);
      setDuplicateCheck({ loading: false, exists: null });
    }
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;
    if (step === 0) {
      if (!formData.first_name.trim()) { errors.first_name = "First name is required"; isValid = false; }
      else if (formData.first_name.trim().length < 2) { errors.first_name = "First name must be at least 2 characters"; isValid = false; }
      if (!formData.last_name.trim()) { errors.last_name = "Last name is required"; isValid = false; }
      else if (formData.last_name.trim().length < 2) { errors.last_name = "Last name must be at least 2 characters"; isValid = false; }
      if (!formData.sex) { errors.sex = "Please select your sex"; isValid = false; }
      if (!formData.birth_date) { errors.birth_date = "Birth date is required"; isValid = false; }
      else {
        const birthDate = new Date(formData.birth_date);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 10 || age > 35) { errors.birth_date = "Age must be between 10 and 35 years old"; isValid = false; }
      }
      if (!formData.civil_status) { errors.civil_status = "Civil status is required"; isValid = false; }
    }
    if (step === 1) {
      if (!formData.contact_number.trim()) { errors.contact_number = "Contact number is required"; isValid = false; }
      else if (!/^[\d\s-+()]+$/.test(formData.contact_number) || formData.contact_number.replace(/\D/g, '').length < 10) {
        errors.contact_number = "Please enter a valid contact number (at least 10 digits)"; isValid = false;
      }
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { errors.email = "Please enter a valid email address"; isValid = false; }
      if (!formData.zone) { errors.zone = "Zone is required"; isValid = false; }
      if (!formData.address.trim()) { errors.address = "Address is required"; isValid = false; }
      else if (formData.address.trim().length < 10) { errors.address = "Please enter a complete address (at least 10 characters)"; isValid = false; }
    }
    if (step === 2) {
      if (!formData.highest_education) { errors.highest_education = "Highest education is required"; isValid = false; }
      if (!formData.work_status) { errors.work_status = "Work status is required"; isValid = false; }
    }
    setFormErrors(errors);
    return isValid;
  };

  const validateAll = (): boolean => {
    let isValid = true;
    for (let i = 0; i < 4; i++) { if (!validateStep(i)) isValid = false; }
    return isValid;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: "" });
    if (name === 'contact_number') checkDuplicateContact(value);
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    if (formErrors[name]) setFormErrors({ ...formErrors, [name]: "" });
  };

  const handleRadioChange = (name: string, value: string) => {
    const boolValue = value === 'yes' ? true : value === 'no' ? false : null;
    setFormData({ ...formData, [name]: boolValue });
  };

  const handleNext = () => { if (validateStep(activeStep)) setActiveStep(prev => Math.min(prev + 1, 3)); };
  const handleBack = () => setActiveStep(prev => Math.max(prev - 1, 0));

  const handleOpenConfirmDialog = () => {
    if (!validateAll()) {
      for (let i = 0; i < 4; i++) { if (!validateStep(i)) { setActiveStep(i); break; } }
      return;
    }
    if (duplicateCheck.exists) {
      setStatus({ success: false, message: "This contact number is already registered." });
      setActiveStep(1);
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmSubmit = async () => {
    setConfirmDialogOpen(false);
    await performSubmit();
  };

  const startCountdown = () => {
    const submissionTime = Date.now();
    localStorage.setItem(SUBMISSION_STORAGE_KEY, submissionTime.toString());
    const initialCountdown = Math.ceil(SUBMISSION_TIMEOUT / 1000);
    setCountdown(initialCountdown);
    setIsSubmitting(true);
    const countdownInterval = setInterval(() => {
      setCountdown(prevCount => {
        if (prevCount <= 1) {
          clearInterval(countdownInterval);
          setIsSubmitting(false);
          localStorage.removeItem(SUBMISSION_STORAGE_KEY);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);
  };

  const performSubmit = async () => {
    setIsSubmitting(true);
    setStatus({ success: null, message: "" });
    try {
      const submissionData = {
        first_name: formData.first_name.trim(), middle_name: formData.middle_name.trim() || null,
        last_name: formData.last_name.trim(), suffix_name: formData.suffix_name === 'None' ? null : formData.suffix_name,
        sex: formData.sex, birth_date: formData.birth_date, civil_status: formData.civil_status,
        email: formData.email.trim() || null, contact_number: formData.contact_number.trim(),
        address: formData.address.trim(), zone: formData.zone, youth_classification: formData.youth_classification || null,
        highest_education: formData.highest_education, work_status: formData.work_status,
        registered_voter: formData.registered_voter,
        voted_last_election: formData.registered_voter === true ? formData.voted_last_election : null,
        attended_sk_assembly: formData.attended_sk_assembly,
        assembly_attendance_count: formData.attended_sk_assembly === true && formData.assembly_attendance_count ? parseInt(formData.assembly_attendance_count, 10) : null,
        device_info: captureDeviceInfo(), user_agent: navigator.userAgent,
        referrer: document.referrer || window.location.href,
      };
      const { data, error } = await supabase.from('youth_profiles').insert([submissionData]).select('id').single();
      if (error) {
        if (error.code === '23505') throw new Error("This contact number is already registered.");
        throw error;
      }
      await supabase.from('form_submissions_log').insert([{
        profile_id: data.id, device_info: captureDeviceInfo(), user_agent: navigator.userAgent,
        referrer: document.referrer || window.location.href, submission_successful: true,
      }]);
      setStatus({ success: true, message: "Registration successful! Thank you for joining the SK Youth Profiling program." });
      setTimeout(() => {
        setFormData({ first_name: "", middle_name: "", last_name: "", suffix_name: "None", sex: "", birth_date: "", civil_status: "", email: "", contact_number: "", address: "", zone: "", youth_classification: "", highest_education: "", work_status: "", registered_voter: null, voted_last_election: null, attended_sk_assembly: null, assembly_attendance_count: "" });
        setActiveStep(0);
        setDuplicateCheck({ loading: false, exists: null });
      }, 3000);
      startCountdown();
    } catch (error: any) {
      console.error('Submission error:', error);
      setStatus({ success: false, message: error.message || "An error occurred. Please try again later." });
      setIsSubmitting(false);
      setTimeout(() => setStatus({ success: null, message: "" }), 10000);
    }
  };

  const formatCountdown = () => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Helper function to get section colors based on dark mode
  const getSectionStyles = (color: string) => ({
    bgcolor: isDarkMode ? `${color}30` : `${color}10`,
    borderLeft: `4px solid ${color}`,
  });

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Fade in={activeStep === 0} timeout={400}>
            <Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: 2, 
                mb: 3, 
                p: 2, 
                borderRadius: 2,
                flexDirection: isMobile ? 'column' : 'row',
                ...getSectionStyles('#1B75BC')
              }}>
                <Person sx={{ color: '#1B75BC', fontSize: isMobile ? 24 : 28 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                    Personal Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Tell us about yourself
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField 
                    fullWidth 
                    label="First Name *" 
                    name="first_name" 
                    value={formData.first_name} 
                    onChange={handleChange} 
                    variant="outlined" 
                    error={!!formErrors.first_name} 
                    helperText={formErrors.first_name} 
                    placeholder="Juan"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField 
                    fullWidth 
                    label="Middle Name" 
                    name="middle_name" 
                    value={formData.middle_name} 
                    onChange={handleChange} 
                    variant="outlined" 
                    error={!!formErrors.middle_name} 
                    helperText={formErrors.middle_name || "Optional"} 
                    placeholder="Dela Cruz"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField 
                    fullWidth 
                    label="Last Name *" 
                    name="last_name" 
                    value={formData.last_name} 
                    onChange={handleChange} 
                    variant="outlined" 
                    error={!!formErrors.last_name} 
                    helperText={formErrors.last_name} 
                    placeholder="Santos"
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Suffix</InputLabel>
                    <Select name="suffix_name" value={formData.suffix_name} onChange={(e) => handleSelectChange('suffix_name', e.target.value)} label="Suffix">
                      {SUFFIX_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option === 'None' ? 'None' : option}</MenuItem>))}
                    </Select>
                    <FormHelperText>Optional</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" required error={!!formErrors.sex} size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Sex *</InputLabel>
                    <Select name="sex" value={formData.sex} onChange={(e) => handleSelectChange('sex', e.target.value)} label="Sex *">
                      {SEX_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                    </Select>
                    {formErrors.sex && <FormHelperText>{formErrors.sex}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Birth Date *" 
                    name="birth_date" 
                    type="date" 
                    value={formData.birth_date} 
                    onChange={handleChange} 
                    variant="outlined" 
                    required 
                    error={!!formErrors.birth_date} 
                    helperText={formErrors.birth_date || "Must be between 10-35 years old"} 
                    InputLabelProps={{ shrink: true }}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" required error={!!formErrors.civil_status} size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Civil Status *</InputLabel>
                    <Select name="civil_status" value={formData.civil_status} onChange={(e) => handleSelectChange('civil_status', e.target.value)} label="Civil Status *">
                      {CIVIL_STATUS_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                    </Select>
                    {formErrors.civil_status && <FormHelperText>{formErrors.civil_status}</FormHelperText>}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        );
      case 1:
        return (
          <Fade in={activeStep === 1} timeout={400}>
            <Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: 2, 
                mb: 3, 
                p: 2, 
                borderRadius: 2,
                flexDirection: isMobile ? 'column' : 'row',
                ...getSectionStyles('#FDB813')
              }}>
                <Phone sx={{ color: '#FDB813', fontSize: isMobile ? 24 : 28 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                    Contact Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    How can we reach you?
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Contact Number *" 
                    name="contact_number" 
                    type="tel"
                    value={formData.contact_number} 
                    onChange={handleChange} 
                    variant="outlined" 
                    required 
                    error={!!formErrors.contact_number || duplicateCheck.exists === true} 
                    helperText={formErrors.contact_number || (duplicateCheck.exists ? "This number is already registered" : "Format: +63 XXX XXX XXXX")} 
                    placeholder="+63 912 345 6789" 
                    InputProps={{ 
                      startAdornment: (<InputAdornment position="start"><Phone sx={{ color: duplicateCheck.exists ? '#ED1C24' : '#FDB813' }} /></InputAdornment>), 
                      endAdornment: duplicateCheck.loading ? <CircularProgress size={20} /> : duplicateCheck.exists === true ? <CheckCircle sx={{ color: '#ED1C24' }} /> : null 
                    }}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Email Address" 
                    name="email" 
                    type="email"
                    value={formData.email} 
                    onChange={handleChange} 
                    variant="outlined" 
                    error={!!formErrors.email} 
                    helperText={formErrors.email || "Optional but recommended"} 
                    placeholder="juandelacruz@email.com" 
                    InputProps={{ startAdornment: (<InputAdornment position="start"><Email sx={{ color: '#FDB813' }} /></InputAdornment>) }}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" required error={!!formErrors.zone} size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Zone *</InputLabel>
                    <Select name="zone" value={formData.zone} onChange={(e) => handleSelectChange('zone', e.target.value)} label="Zone *">
                      {ZONE_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                    </Select>
                    {formErrors.zone && <FormHelperText>{formErrors.zone}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Complete Address *" 
                    name="address" 
                    value={formData.address} 
                    onChange={handleChange} 
                    variant="outlined" 
                    multiline 
                    rows={isMobile ? 2 : 3} 
                    required 
                    error={!!formErrors.address} 
                    helperText={formErrors.address} 
                    placeholder="House #, Street, Barangay, City" 
                    InputProps={{ startAdornment: (<InputAdornment position="start"><LocationOn sx={{ color: '#FDB813' }} /></InputAdornment>) }}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>
              </Grid>
            </Box>
          </Fade>
        );
      case 2:
        return (
          <Fade in={activeStep === 2} timeout={400}>
            <Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: 2, 
                mb: 3, 
                p: 2, 
                borderRadius: 2,
                flexDirection: isMobile ? 'column' : 'row',
                ...getSectionStyles('#ED1C24')
              }}>
                <School sx={{ color: '#ED1C24', fontSize: isMobile ? 24 : 28 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                    Classification & Status
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Help us understand your background
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Youth Classification</InputLabel>
                    <Select name="youth_classification" value={formData.youth_classification} onChange={(e) => handleSelectChange('youth_classification', e.target.value)} label="Youth Classification">
                      <MenuItem value=""><em>None</em></MenuItem>
                      {YOUTH_CLASSIFICATION_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                    </Select>
                    <FormHelperText>Optional: NEET, Working Youth, etc.</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" required error={!!formErrors.highest_education} size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Highest Education *</InputLabel>
                    <Select name="highest_education" value={formData.highest_education} onChange={(e) => handleSelectChange('highest_education', e.target.value)} label="Highest Education *">
                      {EDUCATION_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                    </Select>
                    {formErrors.highest_education && <FormHelperText>{formErrors.highest_education}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" required error={!!formErrors.work_status} size={isMobile ? 'small' : 'medium'}>
                    <InputLabel>Work Status *</InputLabel>
                    <Select name="work_status" value={formData.work_status} onChange={(e) => handleSelectChange('work_status', e.target.value)} label="Work Status *">
                      {WORK_STATUS_OPTIONS.map((option) => (<MenuItem key={option} value={option}>{option}</MenuItem>))}
                    </Select>
                    {formErrors.work_status && <FormHelperText>{formErrors.work_status}</FormHelperText>}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Fade>
        );
      case 3:
        return (
          <Fade in={activeStep === 3} timeout={400}>
            <Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: isMobile ? 'flex-start' : 'center', 
                gap: 2, 
                mb: 3, 
                p: 2, 
                borderRadius: 2,
                flexDirection: isMobile ? 'column' : 'row',
                ...getSectionStyles('#4A5568')
              }}>
                <HowToVote sx={{ color: '#4A5568', fontSize: isMobile ? 24 : 28 }} />
                <Box>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
                    SK Participation
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                    Optional: Your engagement with SK programs
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={isMobile ? 2 : 3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, fontSize: isMobile ? '0.9rem' : '1rem' }}>Registered Voter?</Typography>
                  <RadioGroup row={!isMobile} name="registered_voter" value={formData.registered_voter === null ? '' : formData.registered_voter ? 'yes' : 'no'} onChange={(e) => handleRadioChange('registered_voter', e.target.value)}>
                    <FormControlLabel value="yes" control={<Radio size={isMobile ? 'small' : 'medium'} />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio size={isMobile ? 'small' : 'medium'} />} label="No" />
                  </RadioGroup>
                </Grid>
                {formData.registered_voter === true && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, fontSize: isMobile ? '0.9rem' : '1rem' }}>Voted in Last Election?</Typography>
                    <RadioGroup row={!isMobile} name="voted_last_election" value={formData.voted_last_election === null ? '' : formData.voted_last_election ? 'yes' : 'no'} onChange={(e) => handleRadioChange('voted_last_election', e.target.value)}>
                      <FormControlLabel value="yes" control={<Radio size={isMobile ? 'small' : 'medium'} />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio size={isMobile ? 'small' : 'medium'} />} label="No" />
                    </RadioGroup>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500, fontSize: isMobile ? '0.9rem' : '1rem' }}>Attended SK Assembly?</Typography>
                  <RadioGroup row={!isMobile} name="attended_sk_assembly" value={formData.attended_sk_assembly === null ? '' : formData.attended_sk_assembly ? 'yes' : 'no'} onChange={(e) => handleRadioChange('attended_sk_assembly', e.target.value)}>
                    <FormControlLabel value="yes" control={<Radio size={isMobile ? 'small' : 'medium'} />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio size={isMobile ? 'small' : 'medium'} />} label="No" />
                  </RadioGroup>
                </Grid>
                {formData.attended_sk_assembly === true && (
                  <Grid item xs={12} sm={6}>
                    <TextField 
                      fullWidth 
                      label="How many times attended?" 
                      name="assembly_attendance_count" 
                      type="number" 
                      value={formData.assembly_attendance_count} 
                      onChange={handleChange} 
                      variant="outlined" 
                      inputProps={{ min: 1 }} 
                      helperText="Number of SK assemblies attended"
                      size={isMobile ? 'small' : 'medium'}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Fade>
        );
      default:
        return null;
    }
  };

  return (
    <div id="youth-profiling-form" className="petition-container">
      <Container maxWidth="md" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        <Paper 
          className="input-container" 
          elevation={0} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 4 }, 
            borderRadius: { xs: 2, md: 4 }, 
            background: isDarkMode ? 'background.paper' : 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
            border: isDarkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(27, 117, 188, 0.1)'
          }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 2, md: 4 } }}>
            <Avatar src={basketballIcon} alt="SK Logo" sx={{ 
              width: { xs: 70, md: 100 }, 
              height: { xs: 70, md: 100 }, 
              mx: "auto", 
              mb: 2, 
              border: `3px solid ${isDarkMode ? '#4A90D9' : '#1B75BC'}` 
            }} />
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                background: 'linear-gradient(45deg, #1B75BC 30%, #ED1C24 50%, #FDB813 70%)', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent', 
                fontWeight: 800, 
                letterSpacing: '0.05em', 
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
              }}
            >
              Youth Profiling
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: isDarkMode ? 'primary.light' : '#1B75BC', 
                fontWeight: 600, 
                mb: 0.5,
                fontSize: { xs: '0.9rem', md: '1.25rem' }
              }}
            >
              Sangguniang Kabataan - Guiwan
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            >
              Complete your profile to join our youth database
            </Typography>
          </Box>

          {isMobile ? (
            <MobileStepper
              variant="dots"
              steps={4}
              position="static"
              activeStep={activeStep}
              sx={{ 
                mb: 3, 
                backgroundColor: 'transparent',
                '& .MuiMobileStepper-dot': {
                  width: 10,
                  height: 10,
                  mx: 0.5,
                }
              }}
              nextButton={
                <Button 
                  size="small" 
                  onClick={handleNext} 
                  disabled={activeStep === 3}
                  endIcon={<KeyboardArrowRight />}
                >
                  Next
                </Button>
              }
              backButton={
                <Button 
                  size="small" 
                  onClick={handleBack} 
                  disabled={activeStep === 0}
                  startIcon={<KeyboardArrowLeft />}
                >
                  Back
                </Button>
              }
            />
          ) : (
            <Stepper 
              activeStep={activeStep} 
              alternativeLabel 
              sx={{ 
                mb: 4,
                '& .MuiStepLabel-label': { fontSize: { xs: '0.75rem', md: '0.875rem' }, fontWeight: 500 }
              }}
            >
              {FORM_STEPS.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel sx={{ 
                    '& .MuiStepIcon-root': { 
                      color: index <= activeStep ? step.color : isDarkMode ? 'rgba(255,255,255,0.3)' : '#E2E8F0',
                      '&.Mui-active': { color: step.color },
                      '&.Mui-completed': { color: step.color }
                    } 
                  }}>
                    {step.label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          )}

          <Box component="form" noValidate>
            {renderStepContent(activeStep)}

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              mt: { xs: 3, md: 4 }, 
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Button 
                variant="outlined" 
                onClick={handleBack} 
                disabled={activeStep === 0} 
                startIcon={<ArrowBack />} 
                sx={{ 
                  flex: 1,
                  minHeight: isMobile ? 44 : 'auto',
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.3)' : '#E2E8F0', 
                  color: 'text.secondary',
                  '&:hover': { 
                    borderColor: 'primary.main', 
                    color: 'primary.main',
                    backgroundColor: isDarkMode ? 'rgba(27, 117, 188, 0.1)' : 'rgba(27, 117, 188, 0.05)'
                  } 
                }}
              >
                Back
              </Button>
              {activeStep === 3 ? (
                <Button 
                  onClick={handleOpenConfirmDialog}
                  variant="contained" 
                  disabled={isSubmitting || duplicateCheck.exists === true} 
                  sx={{ 
                    flex: 1,
                    minHeight: isMobile ? 44 : 'auto',
                    background: isSubmitting ? 'rgba(148, 163, 184, 0.5)' : 'linear-gradient(45deg, #1B75BC 30%, #0D5AA7 90%)', 
                    '&:hover': { 
                      background: 'linear-gradient(45deg, #0D5AA7 30%, #1B75BC 90%)' 
                    },
                    '&.Mui-disabled': {
                      background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
                      color: isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)'
                    }
                  }}
                >
                  {isSubmitting ? `Please wait ${formatCountdown()}` : duplicateCheck.exists ? 'Already Registered' : 'Submit Registration'}
                </Button>
              ) : (
                <Button 
                  variant="contained" 
                  onClick={handleNext} 
                  endIcon={<ArrowForward />} 
                  sx={{ 
                    flex: 1,
                    minHeight: isMobile ? 44 : 'auto',
                    background: 'linear-gradient(45deg, #1B75BC 30%, #0D5AA7 90%)', 
                    '&:hover': { 
                      background: 'linear-gradient(45deg, #0D5AA7 30%, #1B75BC 90%)' 
                    } 
                  }}
                >
                  Next Step
                </Button>
              )}
            </Box>

            {status.message && (
              <Box sx={{ mt: 3 }}>
                <Alert 
                  severity={status.success ? "success" : "error"} 
                  sx={{ 
                    borderRadius: 2, 
                    '& .MuiAlert-icon': { fontSize: 28 },
                    backgroundColor: isDarkMode ? (status.success ? 'rgba(5, 150, 105, 0.2)' : 'rgba(220, 38, 38, 0.2)') : undefined,
                    color: isDarkMode ? '#fff' : undefined
                  }}
                >
                  {status.message}
                </Alert>
              </Box>
            )}
          </Box>

          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialogOpen}
            onClose={handleCloseConfirmDialog}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: { xs: 2, md: 3 },
                backgroundColor: isDarkMode ? '#161b22' : '#ffffff',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
              }
            }}
          >
            <DialogTitle 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                pb: 1,
                color: isDarkMode ? '#e6edf3' : '#1a1a2e',
              }}
            >
              <Warning sx={{ color: '#FDB813', fontSize: 28 }} />
              <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                Confirm Registration
              </Typography>
            </DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: isDarkMode ? '#b0b8c4' : '#4a5568', mb: 2 }}>
                Please review your information before submitting. This action cannot be undone.
              </DialogContentText>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa',
                  borderRadius: 2,
                  border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                }}
              >
                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e6edf3' : '#1a1a2e', mb: 1.5, fontWeight: 600 }}>
                  Summary:
                </Typography>
                <List dense sx={{ py: 0 }}>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText 
                      primary="Name" 
                      secondary={`${formData.first_name} ${formData.middle_name ? formData.middle_name + ' ' : ''}${formData.last_name}${formData.suffix_name !== 'None' ? ' ' + formData.suffix_name : ''}`}
                      primaryTypographyProps={{ color: isDarkMode ? '#b0b8c4' : '#4a5568', fontSize: '0.75rem' }}
                      secondaryTypographyProps={{ color: isDarkMode ? '#e6edf3' : '#1a1a2e', fontSize: '0.875rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText 
                      primary="Contact" 
                      secondary={formData.contact_number}
                      primaryTypographyProps={{ color: isDarkMode ? '#b0b8c4' : '#4a5568', fontSize: '0.75rem' }}
                      secondaryTypographyProps={{ color: isDarkMode ? '#e6edf3' : '#1a1a2e', fontSize: '0.875rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText 
                      primary="Zone" 
                      secondary={formData.zone}
                      primaryTypographyProps={{ color: isDarkMode ? '#b0b8c4' : '#4a5568', fontSize: '0.75rem' }}
                      secondaryTypographyProps={{ color: isDarkMode ? '#e6edf3' : '#1a1a2e', fontSize: '0.875rem' }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, py: 0.5 }}>
                    <ListItemText 
                      primary="Education & Work" 
                      secondary={`${formData.highest_education} • ${formData.work_status}`}
                      primaryTypographyProps={{ color: isDarkMode ? '#b0b8c4' : '#4a5568', fontSize: '0.75rem' }}
                      secondaryTypographyProps={{ color: isDarkMode ? '#e6edf3' : '#1a1a2e', fontSize: '0.875rem' }}
                    />
                  </ListItem>
                </List>
                <Divider sx={{ my: 1.5, borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }} />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {formData.registered_voter === true && (
                    <Chip 
                      icon={<Check sx={{ fontSize: 16 }} />} 
                      label="Registered Voter" 
                      size="small" 
                      sx={{ 
                        backgroundColor: 'rgba(5, 150, 105, 0.15)', 
                        color: '#059669',
                        fontSize: '0.75rem',
                        height: 24,
                      }} 
                    />
                  )}
                  {formData.attended_sk_assembly === true && (
                    <Chip 
                      icon={<Check sx={{ fontSize: 16 }} />} 
                      label="SK Assembly Attendee" 
                      size="small" 
                      sx={{ 
                        backgroundColor: 'rgba(27, 117, 188, 0.15)', 
                        color: '#1B75BC',
                        fontSize: '0.75rem',
                        height: 24,
                      }} 
                    />
                  )}
                </Box>
              </Paper>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1, gap: 1 }}>
              <Button 
                onClick={handleCloseConfirmDialog}
                variant="outlined"
                startIcon={<Close />}
                sx={{ 
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                  color: isDarkMode ? '#b0b8c4' : '#4a5568',
                  '&:hover': {
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  }
                }}
              >
                Review Again
              </Button>
              <Button 
                onClick={handleConfirmSubmit}
                variant="contained"
                startIcon={<CheckCircle />}
                disabled={isSubmitting}
                sx={{ 
                  background: 'linear-gradient(45deg, #1B75BC 30%, #0D5AA7 90%)',
                  '&:hover': { 
                    background: 'linear-gradient(45deg, #0D5AA7 30%, #1B75BC 90%)' 
                  },
                }}
              >
                Confirm & Submit
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </div>
  );
}

export default YouthProfilingForm;
